import { useEffect, useState } from "react";
import { CanvasExtras } from "./components/CanvasExtras";
import { ZoomHud } from "./components/Hud";
import { ExportModal, GalleryModal, ShareModal, AuthModal, AboutModal } from "./components/Modals";
import { SidePanels } from "./components/SidePanels";
import { ToolPanel } from "./components/ToolPanel";
import { TopBar } from "./components/TopBar";
import { Viewer } from "./components/Viewer";
import { useEngine } from "./engine/useEngine";
import { store, useAppState } from "./state/store";
import { parseAuthCallback } from "@frond/client";

type Modal = "gallery" | "export" | "share" | "auth" | "about" | null;

export function App() {
  const state = useAppState();
  const { canvasRef, engine, version } = useEngine();
  const [modal, setModal] = useState<Modal>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    void store.boot();
  }, []);

  // Published-canvas viewer route: /view/<token> (read-only, no auth).
  const viewMatch = /^\/view\/([A-Za-z0-9-]+)/.exec(window.location.pathname);
  if (viewMatch) {
    return <Viewer token={viewMatch[1]!} />;
  }

  // OAuth callback: hosted UI redirects back here with ?code=... or ?error=...
  useEffect(() => {
    if (!state.booted || !state.client) return;
    const { code, error, errorDescription } = parseAuthCallback(window.location.search);
    if (code) {
      void store
        .handleCallback(code)
        .then(() => {
          window.history.replaceState({}, "", window.location.pathname);
          setToast("âœ“ Signed in successfully");
          setTimeout(() => setToast(null), 3500);
        })
        .catch((err) => {
          console.error("Auth callback exchange failed", err);
          window.history.replaceState({}, "", window.location.pathname);
          setToast("âš  Sign in failed. Please try again.");
          setTimeout(() => setToast(null), 4000);
        });
    } else if (error) {
      console.warn("Cognito auth error:", error, errorDescription);
      window.history.replaceState({}, "", window.location.pathname);
      setToast(`âš  Authentication: ${errorDescription || error}`);
      setTimeout(() => setToast(null), 4000);
    }
  }, [state.booted, state.client]);

  // Global Ctrl+S / Cmd+S save keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        void store.saveNow().then((saved) => {
          if (saved) {
            setToast("âœ“ Canvas saved");
            setTimeout(() => setToast(null), 2500);
          }
        });
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const [toolPanelOpen, setToolPanelOpen] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 768 : true
  );
  const [sidePanelOpen, setSidePanelOpen] = useState(false);

  if (!state.booted) {
    return <div className="boot-screen">ðŸŒ¿ Loadingâ€¦</div>;
  }

  const handleClearCanvas = () => {
    if (!engine) return;
    if (engine.world.elementCount > 0 && !window.confirm("Clear all drawings and start a new canvas?")) {
      return;
    }
    engine.world.clear();
    engine.history.clear();
    void store.newDocument();
    engine.fitView();
  };

  return (
    <main id="main-content" className="app">
      <div className="canvas-host">
        <canvas ref={canvasRef} role="img" aria-label="Infinite zoom drawing canvas" tabIndex={0} />
      </div>

      {engine && <CanvasExtras engine={engine} />}
      {engine && (
        <ToolPanel
          engine={engine}
          version={version}
          open={toolPanelOpen}
          onToggle={(val) => setToolPanelOpen(val)}
        />
      )}
      {engine && (
        <SidePanels
          engine={engine}
          version={version}
          open={sidePanelOpen}
          onClose={() => setSidePanelOpen(false)}
          onToggle={() => setSidePanelOpen((o) => !o)}
        />
      )}
      {engine && <ZoomHud engine={engine} version={version} />}

      <TopBar
        onOpenGallery={() => setModal("gallery")}
        onOpenExport={() => setModal("export")}
        onOpenShare={() => setModal("share")}
        onOpenAbout={() => setModal("about")}
        onToggleSidePanel={() => setSidePanelOpen((o) => !o)}
        onToggleToolPanel={() => setToolPanelOpen((o) => !o)}
        onClearCanvas={handleClearCanvas}
        toolPanelOpen={toolPanelOpen}
        sidePanelOpen={sidePanelOpen}
      />

      {state.configError && (
        <div className="offline-banner">
          Cloud services are unreachable â€” running locally. Your work is saved in this browser.
        </div>
      )}

      {modal === "gallery" && <GalleryModal onClose={() => setModal(null)} />}
      {modal === "export" && engine && <ExportModal engine={engine} onClose={() => setModal(null)} />}
      {modal === "share" && engine && <ShareModal engine={engine} onClose={() => setModal(null)} />}
      {modal === "auth" && <AuthModal onClose={() => setModal(null)} />}
      {modal === "about" && <AboutModal onClose={() => setModal(null)} />}

      {toast && <div className="app-toast">{toast}</div>}
    </main>
  );
}
